<template>
  <v-container fluid>
    <v-card flat>
      <v-card-title>
        Uncooked Ingredients
        <v-spacer/>
        <v-btn text small @click="clearCache">Clear cache</v-btn>
      </v-card-title>
      <v-card-subtitle>
        Ingredients whose description says they are not cooked — "raw", "dry", "uncooked" — that sit in
        a meal where they would have to be cooked. Many hits are correct (garlic and cilantro really do go
        in raw); others are errors, because nobody eats dry pasta. Work through the three steps: find the
        descriptor words, scan the meals in scope for the ingredients they use, then check every meal where
        a matching ingredient turns up.
      </v-card-subtitle>

      <v-card-text>
        <v-row no-gutters align="center">
          <v-text-field
            v-model.number="months"
            label="Months back"
            type="number"
            dense
            outlined
            hide-details
            style="max-width: 130px"
            class="mr-3"
          />
          <v-select
            v-model="diet"
            :items="dietOptions"
            label="Diet"
            dense
            outlined
            hide-details
            clearable
            style="max-width: 240px"
            class="mr-3"
          />
          <span class="text-caption grey--text">
            One meal per SKU — the most recent version, the one an edit would land on.
          </span>
        </v-row>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000">{{ snackbarText }}</v-snackbar>

    <!-- step 1 — which words describe an uncooked ingredient -->
    <v-card class="mt-4" outlined>
      <v-card-title class="subtitle-1">
        1 &nbsp;Descriptor words
        <v-spacer/>
        <span v-if="wordsCachedAt" class="text-caption grey--text mr-3">loaded {{ wordsCachedAt }}</span>
        <v-btn text small :loading="loadingWords" @click="loadWords(true)">
          {{ wordData ? 'Reload' : 'Load word frequency' }}
        </v-btn>
      </v-card-title>
      <v-card-subtitle v-if="wordData">
        Every word used in the {{ wordData.foods }} ingredient descriptions, commonest first. Click the words
        that mean an ingredient is not cooked.
      </v-card-subtitle>
      <v-card-subtitle v-else>
        Counts the words in the description and simple description of every ingredient, so the descriptors
        worth picking rise to the top.
      </v-card-subtitle>

      <v-card-text v-if="wordData">
        <v-row no-gutters align="center" class="mb-2">
          <v-text-field
            v-model="wordFilter"
            label="Filter words"
            dense
            outlined
            clearable
            hide-details
            style="max-width: 220px"
            class="mr-3"
          />
          <v-checkbox
            v-model="onlyActiveWords"
            :disabled="!activeData"
            label="Only words used by meals in scope"
            dense
            hide-details
            class="mr-3 mt-0 pt-0"
          />
          <v-spacer/>
          <v-btn text small v-if="selectedWords.length" @click="selectedWords = []">Clear selection</v-btn>
        </v-row>

        <div class="mb-3">
          <v-chip
            v-for="w in visibleWords"
            :key="w.word"
            small
            class="ma-1"
            :color="isSelected(w.word) ? 'primary' : undefined"
            :outlined="!isSelected(w.word)"
            @click="toggleWord(w.word)"
          >
            {{ w.word }}
            <span class="ml-1" :class="isSelected(w.word) ? '' : 'grey--text'">{{ w.foods }}</span>
            <span v-if="activeCounts[w.word]" class="ml-1 caption">· {{ activeCounts[w.word] }} in scope</span>
          </v-chip>
          <span v-if="!visibleWords.length" class="text-caption grey--text">no words match that filter</span>
        </div>

        <v-text-field
          v-model="wordText"
          label="Selected words"
          hint="Comma separated — type your own here if a word is not in the list"
          persistent-hint
          dense
          outlined
        />
        <div class="mt-2">
          <v-btn text x-small class="mr-2" v-for="p in presets" :key="p.label" @click="applyPreset(p)">
            {{ p.label }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- step 2 — which of those ingredients are actually in use -->
    <v-card class="mt-4" outlined>
      <v-card-title class="subtitle-1">
        2 &nbsp;Ingredients in use
        <v-spacer/>
        <span v-if="activeCachedAt" class="text-caption grey--text mr-3">loaded {{ activeCachedAt }}</span>
        <v-btn text small :loading="loadingActive" @click="loadActive(true)">
          {{ activeData ? 'Rescan' : 'Scan meals in scope' }}
        </v-btn>
      </v-card-title>
      <v-card-subtitle v-if="activeData">
        {{ activeData.skus }} SKUs in scope use {{ activeData.ingredients.length }} distinct ingredients.
        <b>{{ activeMatches.length }}</b> of them match the selected words.
      </v-card-subtitle>
      <v-card-subtitle v-else>
        Reads the meal food table of the most recent meal of every SKU in scope, so only ingredients that are
        still on a menu get checked.
      </v-card-subtitle>

      <v-card-text v-if="activeData && selectedWords.length">
        <v-chip
          v-for="i in activeMatches.slice(0, 40)"
          :key="i.foodid"
          small
          outlined
          class="ma-1"
          :title="i.description"
        >
          {{ i.simple || i.description }}
          <span class="grey--text ml-1">{{ i.meals }} meals</span>
        </v-chip>
        <span v-if="activeMatches.length > 40" class="text-caption grey--text ml-2">
          and {{ activeMatches.length - 40 }} more
        </span>
      </v-card-text>
    </v-card>

    <!-- step 3 — where each matching ingredient is used -->
    <v-card class="mt-4" outlined>
      <v-card-title class="subtitle-1">
        3 &nbsp;Meals to check
        <v-spacer/>
        <span v-if="matchesCachedAt" class="text-caption grey--text mr-3">loaded {{ matchesCachedAt }}</span>
        <v-btn
          color="primary"
          small
          :disabled="!selectedWords.length"
          :loading="loadingMatches"
          @click="loadMatches(true)"
        >
          {{ matchData ? 'Re-run' : 'Find meals' }}
        </v-btn>
      </v-card-title>
      <v-card-subtitle v-if="!selectedWords.length">
        Pick at least one word in step 1 first.
      </v-card-subtitle>
      <v-card-subtitle v-else-if="matchData">
        {{ groups.length }} ingredients across {{ matchData.uses }} meal entries, looking for
        {{ matchData.scope.words.join(', ') }}.
      </v-card-subtitle>

      <v-card-text v-if="matchData">
        <v-row no-gutters align="center" class="mb-2">
          <v-text-field
            v-model="rowFilter"
            label="Filter ingredients"
            dense
            outlined
            clearable
            hide-details
            style="max-width: 280px"
            class="mr-3"
          />
          <v-select
            v-model="show"
            :items="showOptions"
            label="Show"
            dense
            outlined
            hide-details
            style="max-width: 260px"
            class="mr-3"
          />
          <v-checkbox
            v-model="hideDismissed"
            label="Hide checked"
            dense
            hide-details
            class="mr-3 mt-0 pt-0"
          />
          <v-spacer/>
          <v-btn text small v-if="dismissed.length" @click="clearDismissed">
            Un-check all ({{ dismissed.length }})
          </v-btn>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="visibleGroups"
          item-key="foodid"
          show-expand
          :expanded.sync="expanded"
          :items-per-page="25"
          dense
        >
          <template v-slot:item.simple="{item}">
            <span :class="{'text-decoration-line-through': isDismissed(item.foodid)}">
              {{ item.simple || '—' }}
            </span>
          </template>

          <template v-slot:item.yield="{item}">
            {{ formatAmount(item.yield === null || item.yield === undefined ? 1 : item.yield) }}
          </template>

          <template v-slot:item.flags="{item}">
            <v-chip v-if="item.conflict" x-small color="red lighten-4" class="mr-1"
                    title="The two descriptions disagree about whether it is cooked">disagree</v-chip>
            <v-chip v-for="w in item.matched" :key="w" x-small outlined class="mr-1">{{ w }}</v-chip>
          </template>

          <template v-slot:item.actions="{item}">
            <div class="d-flex justify-end flex-nowrap">
              <v-btn x-small text outlined class="mr-1" v-if="!readOnly" @click="openReplace(item)">Replace</v-btn>
              <v-btn x-small text @click="toggleDismissed(item.foodid)">
                {{ isDismissed(item.foodid) ? 'Undo' : 'Checked' }}
              </v-btn>
            </div>
          </template>

          <template v-slot:expanded-item="{headers: h, item}">
            <td :colspan="h.length" class="pa-0">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Meal</th>
                    <th>SKU</th>
                    <th>Date</th>
                    <th>Diet</th>
                    <th>Component</th>
                    <th class="text-right">Amount</th>
                    <th class="text-right">Yield override</th>
                    <th>Prep</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in item.rows" :key="row.mealFoodId">
                    <td>
                      <router-link :to="mealLink(row)">{{ row.meal.name || `meal ${row.meal.id}` }}</router-link>
                    </td>
                    <td>{{ row.meal.sku }}</td>
                    <td>{{ formatDate(row.meal.date) }}</td>
                    <td>{{ row.meal.dietName }}</td>
                    <td>{{ row.componentName || '—' }}</td>
                    <td class="text-right">{{ formatAmount(row.amount) }}</td>
                    <td class="text-right">{{ row.mealYield === null ? '' : formatAmount(row.mealYield) }}</td>
                    <td>{{ row.prepInstruction }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- replacing an ingredient everywhere it was found -->
    <v-dialog v-model="showReplace" max-width="760">
      <v-card v-if="replaceTarget">
        <v-card-title class="subtitle-1">Replace ingredient</v-card-title>
        <v-card-text>
          <p class="mb-1">
            Replacing <b>{{ replaceTarget.simple || replaceTarget.description }}</b>
            <span class="grey--text"> [{{ replaceTarget.foodid }}]</span>
          </p>
          <p class="caption grey--text">{{ replaceTarget.description }}</p>

          <IngredientAutocomplete @updateValue="replacement = $event"/>

          <p class="caption mt-2">
            A meal component cannot hold the same ingredient twice, so a replacement that is already in the
            same component of a meal will fail for that meal; the rest still go through. Every change is
            recorded against the meal and can be restored from its edit history.
          </p>

          <p class="mb-1">This replaces it in {{ replaceTarget.rows.length }} meals:</p>
          <ul class="caption" style="max-height: 220px; overflow-y: auto">
            <li v-for="row in replaceTarget.rows" :key="row.mealFoodId">
              {{ row.meal.name || `meal ${row.meal.id}` }} ({{ row.meal.id }})
              <span v-if="row.componentName"> — {{ row.componentName }}</span>
            </li>
          </ul>

          <v-alert v-if="replaceErrors.length" type="warning" dense text class="mt-2">
            <div v-for="(e, i) in replaceErrors" :key="i" class="caption">{{ e }}</div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showReplace = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!replacement || !replacement.foodid" :loading="replacing"
                 @click="applyReplace">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from '@/api';
import {mapActions, mapState} from 'vuex';
import IngredientAutocomplete from '@/components/IngredientAutocomplete';

// bump when the cached shape changes, so old entries are ignored
const CACHE_PREFIX = 'uncooked-audit-v1';

// a starting point for the words that mean "not cooked"
const PRESETS = [
  {label: 'raw / dry', words: ['raw', 'dry', 'uncooked', 'dried']},
  {label: 'add frozen', words: ['raw', 'dry', 'uncooked', 'dried', 'frozen', 'unprepared']},
];

function readCache(key) {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}-${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.warn('uncooked audit: could not read cache', key, e);
    return null;
  }
}

// step 3 runs to a megabyte or so, and one entry is kept per word set, so
// older results of the same step make room for the new one
function dropOtherEntries(prefix, keep) {
  try {
    const stale = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${CACHE_PREFIX}-${prefix}`) && k !== keep) stale.push(k);
    }
    stale.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    console.warn('uncooked audit: could not drop older results', e);
  }
}

function writeCache(key, value) {
  const storageKey = `${CACHE_PREFIX}-${key}`;
  if (key.startsWith('matches-')) dropOtherEntries('matches-', storageKey);
  try {
    localStorage.setItem(storageKey, JSON.stringify({...value, cachedAt: Date.now()}));
    return true;
  } catch (e) {
    console.warn('uncooked audit: could not write cache', key, e);
    return false;
  }
}

// drops the cached step results but keeps what the user chose: the selected
// words and the ingredients they have already checked off
function clearCachedKeys() {
  const kept = [`${CACHE_PREFIX}-selected`, `${CACHE_PREFIX}-dismissed`];
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX) && !kept.includes(k)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    console.warn('uncooked audit: could not clear cache', e);
  }
}

function parseWordText(text) {
  const out = new Set();
  for (const part of String(text || '').split(/[\s,;]+/)) {
    const word = part.trim().toLowerCase().replace(/[^a-z-]/g, '');
    if (word.length >= 3) out.add(word);
  }
  return [...out];
}

function hasWord(text, word) {
  return new RegExp(`(^|[^a-z])${word}([^a-z]|$)`, 'i').test(String(text || ''));
}

export default {
  name: 'UncookedIngredients',
  components: {IngredientAutocomplete},
  data() {
    return {
      months: 12,
      diet: null,
      readOnly: false,

      wordData: null,
      activeData: null,
      matchData: null,
      loadingWords: false,
      loadingActive: false,
      loadingMatches: false,

      wordText: '',
      wordFilter: '',
      onlyActiveWords: false,
      rowFilter: '',
      show: 'all',
      showOptions: [
        {text: 'Everything found', value: 'all'},
        {text: 'Descriptions disagree', value: 'conflict'},
      ],
      hideDismissed: true,
      dismissed: [],
      expanded: [],

      showReplace: false,
      replaceTarget: null,
      replacement: null,
      replacing: false,
      replaceErrors: [],

      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      presets: PRESETS,
      headers: [
        {text: 'Ingredient', value: 'simple'},
        {text: 'Description', value: 'description'},
        {text: 'Yield', value: 'yield', align: 'end', width: 80},
        {text: 'Meals', value: 'mealCount', align: 'end', width: 80},
        {text: 'Flags', value: 'flags', sortable: false},
        {text: '', value: 'actions', sortable: false, align: 'end', width: 210},
      ],
    };
  },
  computed: {
    ...mapState(['diets']),
    dietOptions() {
      const diets = this.diets || {};
      return Object.values(diets)
        .map((d) => ({text: d.name, value: d.id}))
        .sort((a, b) => String(a.text).localeCompare(String(b.text)));
    },
    selectedWords() {
      return parseWordText(this.wordText);
    },
    // the scope a cached result belongs to; a different scope is a different cache entry
    scopeKey() {
      return `${this.months}-${this.diet || 'all'}`;
    },
    activeCounts() {
      const counts = {};
      for (const w of (this.activeData && this.activeData.words) || []) counts[w.word] = w.foods;
      return counts;
    },
    visibleWords() {
      const words = (this.wordData && this.wordData.words) || [];
      const f = (this.wordFilter || '').trim().toLowerCase();
      return words.filter((w) => {
        if (f && !w.word.includes(f)) return false;
        if (this.onlyActiveWords && !this.activeCounts[w.word]) return false;
        return true;
      }).slice(0, 400);
    },
    activeMatches() {
      const ingredients = (this.activeData && this.activeData.ingredients) || [];
      if (!this.selectedWords.length) return [];
      return ingredients.filter((i) =>
        this.selectedWords.some((w) => hasWord(i.description, w) || hasWord(i.simple, w)));
    },
    // the api already groups by ingredient; this hangs the meal and component
    // each use belongs to back onto it, and works out the two warning flags
    groups() {
      const data = this.matchData;
      if (!data) return [];
      const meals = new Map((data.meals || []).map((m) => [m.id, m]));
      const components = new Map((data.components || []).map((c) => [c.id, c.name]));
      return (data.ingredients || []).map((ingredient) => ({
        ...ingredient,
        conflict: (ingredient.cooked || []).length > 0,
        mealCount: ingredient.uses.length,
        rows: ingredient.uses.map((use) => ({
          ...use,
          meal: meals.get(use.mealId) || {id: use.mealId},
          componentName: components.get(use.componentId) || null,
        })),
      }));
    },
    visibleGroups() {
      const f = (this.rowFilter || '').trim().toLowerCase();
      return this.groups.filter((g) => {
        if (this.hideDismissed && this.isDismissed(g.foodid)) return false;
        if (this.show === 'conflict' && !g.conflict) return false;
        if (!f) return true;
        // the ingredient's own two names only — matching the meals it is used
        // in made this useless, because garlic is in 1500 meals and so matched
        // almost any word
        return String(g.simple || '').toLowerCase().includes(f)
          || String(g.description || '').toLowerCase().includes(f);
      });
    },
    wordsCachedAt() {
      return this.formatCachedAt(this.wordData);
    },
    activeCachedAt() {
      return this.formatCachedAt(this.activeData);
    },
    matchesCachedAt() {
      return this.formatCachedAt(this.matchData);
    },
  },
  watch: {
    // a different scope invalidates what is on screen, not what is cached
    scopeKey() {
      this.activeData = readCache(`active-${this.scopeKey}`);
      this.matchData = readCache(`matches-${this.scopeKey}-${this.selectedWords.join('-')}`);
    },
    selectedWords(words) {
      try {
        localStorage.setItem(`${CACHE_PREFIX}-selected`, JSON.stringify(words));
      } catch (e) {
        console.warn('uncooked audit: could not save the selected words', e);
      }
      const cached = readCache(`matches-${this.scopeKey}-${words.join('-')}`);
      if (cached) this.matchData = cached;
    },
    dismissed(list) {
      try {
        localStorage.setItem(`${CACHE_PREFIX}-dismissed`, JSON.stringify(list));
      } catch (e) {
        console.warn('uncooked audit: could not save the checked ingredients', e);
      }
    },
  },
  methods: {
    ...mapActions(['fetchDiets']),
    notify(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    formatCachedAt(data) {
      if (!data || !data.cachedAt) return '';
      return new Date(data.cachedAt).toLocaleString();
    },
    formatDate(date) {
      return date ? String(date).slice(0, 10) : '';
    },
    // floats out of postgres carry binary noise (1.1299999952316284), and
    // three decimals is plenty for a yield or a portion
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '';
      const n = Number(amount);
      return Number.isFinite(n) ? Math.round(n * 1000) / 1000 : '';
    },
    mealLink(row) {
      const date = this.formatDate(row.meal.date);
      if (!date) return {name: 'MealDesign', params: {id: String(row.meal.id)}};
      return {name: 'MealDetail', params: {date, id: String(row.meal.id)}};
    },
    isSelected(word) {
      return this.selectedWords.includes(word);
    },
    toggleWord(word) {
      const words = this.selectedWords;
      this.wordText = (this.isSelected(word) ? words.filter((w) => w !== word) : [...words, word]).join(', ');
    },
    applyPreset(preset) {
      this.wordText = preset.words.join(', ');
    },
    isDismissed(foodid) {
      return this.dismissed.includes(foodid);
    },
    toggleDismissed(foodid) {
      this.dismissed = this.isDismissed(foodid)
        ? this.dismissed.filter((id) => id !== foodid)
        : [...this.dismissed, foodid];
    },
    clearDismissed() {
      this.dismissed = [];
    },
    clearCache() {
      clearCachedKeys();
      this.wordData = null;
      this.activeData = null;
      this.matchData = null;
      this.notify('Cleared the cached results');
    },

    async loadWords(force = false) {
      const cached = readCache('words');
      if (cached && !force) {
        this.wordData = cached;
        return;
      }
      this.loadingWords = true;
      try {
        const {data} = await api.get('v2/uncooked-audit/words');
        this.wordData = {...data, cachedAt: Date.now()};
        writeCache('words', data);
      } catch (e) {
        console.error('failed to load the word frequency', e);
        this.notify('Could not load the word frequency', 'error');
      } finally {
        this.loadingWords = false;
      }
    },

    async loadActive(force = false) {
      const key = `active-${this.scopeKey}`;
      const cached = readCache(key);
      if (cached && !force) {
        this.activeData = cached;
        return;
      }
      this.loadingActive = true;
      try {
        const params = {months: this.months};
        if (this.diet) params.diet = this.diet;
        const {data} = await api.get('v2/uncooked-audit/active', {params});
        this.activeData = {...data, cachedAt: Date.now()};
        writeCache(key, data);
      } catch (e) {
        console.error('failed to scan the meals in scope', e);
        this.notify('Could not scan the meals in scope', 'error');
      } finally {
        this.loadingActive = false;
      }
    },

    async loadMatches(force = false) {
      const words = this.selectedWords;
      if (!words.length) return;
      const key = `matches-${this.scopeKey}-${words.join('-')}`;
      const cached = readCache(key);
      if (cached && !force) {
        this.matchData = cached;
        return;
      }
      this.loadingMatches = true;
      try {
        const body = {words, months: this.months};
        if (this.diet) body.diet = this.diet;
        const {data} = await api.post('v2/uncooked-audit/matches', body);
        this.matchData = {...data, cachedAt: Date.now()};
        if (!writeCache(key, data)) {
          this.notify('Found the meals, but the result was too big to cache', 'warning');
        }
      } catch (e) {
        console.error('failed to find the meals', e);
        this.notify('Could not find the meals', 'error');
      } finally {
        this.loadingMatches = false;
      }
    },

    openReplace(group) {
      this.replaceTarget = group;
      this.replacement = null;
      this.replaceErrors = [];
      this.showReplace = true;
    },

    // the api replaces one ingredient in one meal component at a time, the
    // same call the ingredients page makes, so this walks every place the
    // audit found it
    async applyReplace() {
      const group = this.replaceTarget;
      const newFoodId = this.replacement && this.replacement.foodid;
      if (!group || !newFoodId) return;
      this.replacing = true;
      this.replaceErrors = [];
      let replaced = 0;
      try {
        for (const row of group.rows) {
          try {
            await api.put(`v2/meal/${row.meal.id}/ingredients/replace`, {
              oldFoodId: group.foodid,
              newFoodId,
              componentId: row.componentId,
            });
            replaced++;
          } catch (e) {
            const meal = row.meal.name || `meal ${row.meal.id}`;
            this.replaceErrors.push(`${meal}: ${(e.response && e.response.data && e.response.data.error) || e.message}`);
          }
        }
        if (replaced) {
          this.notify(`Replaced the ingredient in ${replaced} meals`);
          // the cached rows still name the old ingredient, so re-run step 3
          await this.loadMatches(true);
        }
        if (!this.replaceErrors.length) this.showReplace = false;
      } finally {
        this.replacing = false;
      }
    },
  },
  mounted() {
    this.readOnly = !!this.$route.meta.readOnly;
    this.fetchDiets();

    const savedWords = readCache('selected');
    this.wordText = Array.isArray(savedWords) ? savedWords.join(', ') : PRESETS[0].words.join(', ');
    const savedDismissed = readCache('dismissed');
    if (Array.isArray(savedDismissed)) this.dismissed = savedDismissed;

    this.wordData = readCache('words');
    this.activeData = readCache(`active-${this.scopeKey}`);
    this.matchData = readCache(`matches-${this.scopeKey}-${this.selectedWords.join('-')}`);
  },
};
</script>
